import { useCallback, useEffect, useRef, useState } from "react";
import { Hourglass, Minus, Plus, Play, Square, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader, EmptyState } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { KidAvatar } from "@/components/Avatars";
import { useLiveRefresh } from "@/lib/live";
import { useLook } from "@/context/LookContext";
import { chime, fmtLeft, useCountdown } from "@/lib/screen";

const KBTN = "inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl px-5 font-display text-base font-extrabold";
const LIMITS = [15, 30, 45, 60, 90, 120, 180, 240, 360, 480];

function TimesUp({ onDone, muted }) {
  useEffect(() => { chime(muted); }, [muted]);
  return (
    <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-6 bg-[rgb(var(--glow-kids))] p-6 text-center text-white" data-testid="times-up-card">
      <Hourglass className="h-24 w-24 animate-bounce" />
      <p className="font-display text-5xl font-extrabold">Screen time's up!</p>
      <button className={`${KBTN} bg-white text-[rgb(var(--glow-kids))]`} onClick={onDone} data-testid="times-up-ok">OK</button>
    </div>
  );
}

function Running({ v, serverNow, canStop, parent, onAct }) {
  const left = useCountdown(v.session, serverNow);
  const warned = useRef(false);
  useEffect(() => { if (!parent && left > 0 && left <= 300 && !warned.current) { warned.current = true; toast("5 minutes of screen time left"); } }, [left, parent]);
  return (
    <div className="rounded-3xl border-2 p-6 text-center" style={{ borderColor: "rgba(var(--glow-kids),0.45)" }} data-testid={`session-${v.kid.id}`}>
      <p className="text-sm font-bold uppercase tracking-[0.14em] text-q-muted">Screen time left{v.session.what_for ? ` · ${v.session.what_for}` : ""}</p>
      <p className="font-display text-6xl font-extrabold tabular-nums text-q-text" data-testid={`countdown-${v.kid.id}`}>{fmtLeft(left)}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {parent && <><button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => onAct("extend", { minutes: 5 })} data-testid={`extend-5-${v.kid.id}`}>+5</button>
          <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => onAct("extend", { minutes: 15 })} data-testid={`extend-15-${v.kid.id}`}>+15</button></>}
        {canStop && <button className={parent ? "q-btn q-btn-primary min-h-[44px]" : `${KBTN} bg-[rgb(var(--glow-kids))] text-white`} onClick={() => onAct("end")} data-testid={`stop-${v.kid.id}`}><Square className="h-4 w-4" /> {parent ? "End" : "Stop early"}</button>}
      </div>
    </div>
  );
}

function Ask({ hubId, v, whatFor, onClose, onDone }) {
  const [m, setM] = useState(15);
  const [custom, setCustom] = useState("");
  const [wf, setWf] = useState("TV");
  const [err, setErr] = useState("");
  const go = async () => {
    setErr("");
    try { await api.post(`/organizations/${hubId}/screen-time/${v.kid.id}/requests`, { minutes: custom ? Number(custom) : m, what_for: wf }); toast.success("Sent to a grown-up"); onDone(); } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <FormDialog open onClose={onClose} title="Use screen time" description={`You have ${v.minutes} minutes`} testid="screen-ask-dialog">
      <div className="space-y-3">
        <div className="grid grid-cols-4 gap-2">{[15, 30, 45, 60].map((n) => <button key={n} className={`${KBTN} px-0 ${!custom && m === n ? "bg-[rgb(var(--glow-kids))] text-white" : "border border-q-line text-q-text"}`} onClick={() => { setM(n); setCustom(""); }} data-testid={`ask-${n}`}>{n}</button>)}</div>
        <input type="number" min="5" className="q-input min-h-[56px] text-lg" placeholder="Other minutes" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="ask-custom" />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{whatFor.map((w) => <button key={w} className={`${KBTN} px-2 ${wf === w ? "bg-[rgb(var(--glow-kids))] text-white" : "border border-q-line text-q-text"}`} onClick={() => setWf(w)} data-testid={`ask-for-${w}`}>{w}</button>)}</div>
        {err && <p className="q-error" data-testid="ask-error">{err}</p>}
        <button className={`${KBTN} w-full bg-[rgb(var(--glow-kids))] text-white`} onClick={go} data-testid="ask-submit">Ask</button>
      </div>
    </FormDialog>
  );
}

function ParentTools({ hubId, v, onAct, reload }) {
  const [adj, setAdj] = useState({ minutes: "", note: "" });
  const [start, setStart] = useState("30");
  const setting = async (body) => { try { await api.put(`/organizations/${hubId}/screen-time/${v.kid.id}/settings`, body); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3" data-testid={`screen-tools-${v.kid.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex min-h-[44px] items-center gap-2 text-sm font-bold text-q-text"><input type="checkbox" className="h-5 w-5" checked={v.on} onChange={(e) => setting({ on: e.target.checked })} data-testid={`screen-toggle-${v.kid.id}`} /> Screen time</label>
        {v.on && <label className="flex items-center gap-2 text-sm text-q-muted">Daily limit
          <select className="q-input min-h-[44px] w-36" value={v.daily_limit ?? "none"} onChange={(e) => setting(e.target.value === "none" ? { no_limit: true } : { daily_limit: Number(e.target.value) })} data-testid={`screen-limit-${v.kid.id}`}>
            {LIMITS.map((n) => <option key={n} value={n}>{`${n} min`}</option>)}<option value="none">No limit</option>
          </select></label>}
      </div>
      <p className="text-xs text-q-faint" data-testid={`honor-${v.kid.id}`}>{v.honor}</p>
      {v.on && <>
        <div className="flex flex-wrap items-center gap-2">
          <input type="number" min="1" className="q-input min-h-[44px] w-24" placeholder="Min" value={adj.minutes} onChange={(e) => setAdj({ ...adj, minutes: e.target.value })} data-testid={`adj-minutes-${v.kid.id}`} />
          <input className="q-input min-h-[44px] min-w-0 flex-1" maxLength={80} placeholder="Note" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} data-testid={`adj-note-${v.kid.id}`} />
          <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => onAct("adjust", { minutes: Number(adj.minutes), mode: "add", note: adj.note }).then((ok) => ok && setAdj({ minutes: "", note: "" }))} data-testid={`adj-add-${v.kid.id}`}><Plus className="h-4 w-4" /> Add</button>
          <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => onAct("adjust", { minutes: Number(adj.minutes), mode: "take", note: adj.note }).then((ok) => ok && setAdj({ minutes: "", note: "" }))} data-testid={`adj-take-${v.kid.id}`}><Minus className="h-4 w-4" /> Take</button>
        </div>
        {!v.session && <div className="flex flex-wrap items-center gap-2">
          <select className="q-input min-h-[44px] w-28" value={start} onChange={(e) => setStart(e.target.value)} data-testid={`start-minutes-${v.kid.id}`}>{[15, 30, 45, 60].map((n) => <option key={n} value={n}>{`${n} min`}</option>)}</select>
          <button className="q-btn q-btn-primary min-h-[44px]" onClick={() => onAct("start", { minutes: Number(start) })} data-testid={`start-screen-${v.kid.id}`}><Play className="h-4 w-4" /> Start screen time</button>
        </div>}
      </>}
    </div>
  );
}

function KidBank({ hubId, v, serverNow, parent, whatFor, reload, muted }) {
  const [asking, setAsking] = useState(false);
  const act = async (path, body = {}) => {
    try { const { data } = await api.post(`/organizations/${hubId}/screen-time/${v.kid.id}/${path}`, body); if (path === "end" && data.returned) toast.success(`${data.returned} minutes back in the bank`); reload(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const up = !parent && v.times_up;
  const pend = v.requests.find((r) => r.status === "pending");
  const declined = v.requests.find((r) => r.status === "declined");
  return (
    <section className="q-card space-y-4" data-testid={`screen-kid-${v.kid.id}`}>
      <div className="flex items-center gap-3">
        <KidAvatar avatar={v.kid.avatar} size={44} />
        <h2 className="flex-1 font-display text-xl font-extrabold text-q-text">{v.kid.name}</h2>
        {v.on && <p className="font-display text-3xl font-extrabold text-q-text" data-testid={`bank-${v.kid.id}`}>{v.minutes} <span className="text-base text-q-muted">min</span></p>}
      </div>
      {v.on && v.daily_limit != null && <p className="text-sm text-q-muted" data-testid={`used-${v.kid.id}`}>{v.used_today} of {v.daily_limit} minutes used today</p>}
      {v.session && <Running v={v} serverNow={serverNow} canStop parent={parent} onAct={act} />}
      {!parent && v.on && !v.session && <button className={`${KBTN} w-full bg-[rgb(var(--glow-kids))] text-white`} onClick={() => setAsking(true)} disabled={!!pend} data-testid="use-screen-btn"><Timer className="h-5 w-5" /> {pend ? `Waiting for a grown-up (${pend.minutes} min)` : "Use screen time"}</button>}
      {!parent && declined && !pend && <p className="rounded-2xl bg-q-raised p-3 text-sm font-semibold text-q-muted" data-testid="screen-declined">Not right now: {declined.note}</p>}
      {parent && <ParentTools hubId={hubId} v={v} onAct={act} reload={reload} />}
      {!parent && <p className="text-xs text-q-faint">{v.honor}</p>}
      {v.on && v.history.length > 0 && (
        <details className="text-sm" data-testid={`screen-history-${v.kid.id}`}>
          <summary className="min-h-[44px] cursor-pointer py-2 font-semibold text-q-text">History</summary>
          <ul className="divide-y divide-q-line">{v.history.slice(0, 30).map((h) => (
            <li key={h.id} className="flex justify-between py-2"><span className="text-q-text">{h.label}<span className="ml-2 text-xs text-q-faint">{fmtDate(h.created_at, { month: "short", day: "numeric" })}</span></span>
              <span className={`font-bold tabular-nums ${h.minutes < 0 ? "text-q-muted" : "text-q-text"}`}>{h.minutes > 0 ? "+" : ""}{h.minutes} min</span></li>
          ))}</ul>
        </details>
      )}
      {asking && <Ask hubId={hubId} v={v} whatFor={whatFor} onClose={() => setAsking(false)} onDone={() => { setAsking(false); reload(); }} />}
      {up && <TimesUp muted={muted} onDone={() => api.post(`/organizations/${hubId}/screen-time/${v.kid.id}/times-up-seen`).then(reload)} />}
    </section>
  );
}

export default function ScreenTime() {
  const { hub } = useHub();
  const { look } = useLook();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/screen-time`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => ["screen_time", "kid_request", "chore"].includes(m.entity), 15000);
  // Re-check when the local countdown should have reached zero, so the times-up card shows without waiting for the poll.
  useEffect(() => {
    if (!d) return undefined;
    const skew = new Date(d.server_now).getTime() - Date.now();
    const ends = d.kids.filter((k) => k.session).map((k) => new Date(k.session.end).getTime() - (Date.now() + skew));
    if (!ends.length) return undefined;
    const t = setTimeout(load, Math.max(1000, Math.min(...ends) + 2500));
    return () => clearTimeout(t);
  }, [d, load]);
  if (!d) return <Loader />;
  const parent = hub.my_role !== "kid";
  const kids = parent ? d.kids : d.kids.filter((k) => k.on);
  return (
    <div data-testid="screen-time-page">
      <PageHeader eyebrow="Kids Corner" title="Screen Time" rgb="var(--glow-kids)" testid="screen-time-title" />
      {!kids.length ? <EmptyState title="Screen time is off" hint="Ask a grown-up to switch it on." testid="screen-empty" /> : (
        <div className="grid gap-5 lg:grid-cols-2">{kids.map((v) => <KidBank key={v.kid.id} hubId={hub.id} v={v} serverNow={d.server_now} parent={parent && d.can_manage} whatFor={d.what_for} reload={load} muted={look?.sound_muted} />)}</div>
      )}
    </div>
  );
}
